import { useState, useEffect } from 'react';
import { FINGER_TYPE, HAND_TYPE, GAME_START_KEY } from '../../../constants/Consts';
import { HandElement } from './HandElement';
import { keyInfo } from '../../../types/Typing';
import { TypingResult } from '../../../api';

interface StartModalProps {
  typingResult: TypingResult;
  isStartTyping: boolean;
  startTypingCount: number | undefined;
  handleGetStyle: (tailwindStyle: string) => string;
  showTutorial: boolean;
}

const handTypes = [HAND_TYPE.LIFT, HAND_TYPE.RIGHT];
const fingerTypes: FINGER_TYPE[] = [FINGER_TYPE.LITTLE, FINGER_TYPE.RING, FINGER_TYPE.MIDDLE, FINGER_TYPE.INDEX, FINGER_TYPE.THUMB];
const keyList: keyInfo[] = handTypes.flatMap((hand) => fingerTypes.map((finger) => ({ handType: hand, fingerType: finger })));
const RED_FINGER_RULE_DESC = 'あかいゆびでうとう';

export const StartModal = ({ typingResult, isStartTyping, startTypingCount, handleGetStyle, showTutorial }: StartModalProps) => {
  const { description, clearScore } = typingResult;
  const isShowStart: boolean = startTypingCount === 0;
  const [currentKeyInfo, setCurrentKeyInfo] = useState<keyInfo>(keyList[0]);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>(undefined);

  // 1秒ごとに赤い指を変える
  useEffect(() => {
    if (!isStartTyping && !startTypingCount && !isShowStart && description === RED_FINGER_RULE_DESC) {
      const interval = setInterval(() => {
        setCurrentKeyInfo(() => {
          const i = Math.floor(Math.random() * 10);
          return keyList[i];
        });
      }, 1000);

      setIntervalId(interval);
      return () => clearInterval(interval);
    }
  }, [isStartTyping, startTypingCount, isShowStart, description]);

  // スペースキーが押されたら赤い指用のintervalを解除
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== GAME_START_KEY) return;
      clearInterval(intervalId);
      setIntervalId(undefined);
    };

    if (intervalId === undefined) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [intervalId]);

  return (
    <>
      {isShowStart && (
        <img
          className="absolute w-full object-contain m-auto top-0 left-0 bottom-0 right-0 animate-shrink-fade"
          src={`/common/start_modal/start.webp`}
          alt={'スタート'}
        />
      )}
      <div
        className="absolute
       h-fit m-auto top-0 left-0 bottom-0 right-0 flex items-center justify-center"
      >
        {!isShowStart && startTypingCount && (
          <div className="w-[50%] flex items-center justify-center">
            <img
              key={startTypingCount}
              className="object-contain w-[45%] animate-shrink-fade"
              src={`/common/start_modal/count_${startTypingCount}.webp`}
              alt={'カウント'}
            />
          </div>
        )}
        {!isStartTyping && !startTypingCount && !isShowStart && !showTutorial && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-30"></div>
            <div className="flex flex-col items-center text-main_text mb-2 w-auto min-w-[45%] max-w-[70%] rounded-2xl bg-gradient-to-b from-rule_border_t to-rule_border_b p-[5px] z-50">
              <div className="flex flex-col gap-1 h-full w-full items-center justify-center bg-white rounded-xl p-4 font-black">
                <p className="text-2xl">ルール</p>
                <p className={`font-bold ${handleGetStyle('text-lg pc:text-xl')}`}>スコア{clearScore}でクリア</p>
                <p className={`font-black text-center pt-2 ${handleGetStyle('text-3xl tb:text-4xl pc:text-5xl')}`}>{description}</p>
                {typingResult.description === RED_FINGER_RULE_DESC && (
                  <>
                    <div className={`flex w-full justify-center space-x-8 ${handleGetStyle('h-16 pc:h-20')}`}>
                      <HandElement width="20%" handType={HAND_TYPE.LIFT} currentKeyInfo={currentKeyInfo} />
                      <HandElement width="20%" handType={HAND_TYPE.RIGHT} currentKeyInfo={currentKeyInfo} />
                    </div>
                  </>
                )}
                <div className="pt-2 w-full flex flex-col items-center justify-center gap-[2%] text-sub_text font-bold">
                  <span className="text-sm">＼スタート／</span>
                  <span className="text-2xl px-[8%] py-[1%] border border-main_key rounded select-none font-extrabold bg-current_key">スペース</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
